import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'
import { Field } from 'formik'
import { withTranslationCustom } from 'lib/i18next'

import FormElement from './FormElement'
import Icon from 'components/Icon'
import Input from './Input'
import ExtendedTimeKeeper from 'components/Forms/ExtendedTimeKeeper/ExtendedTimeKeeper'
import Button from 'components/UI/Button'
import ExtendedSingleDatePicker from 'components/Forms/Date/ExtendedSingleDatePicker/ExtendedSingleDatePicker'

import './date-time-input.pcss'

const toDateTimeString = (newDate, newTime) => {
    const split = newTime.split(':')

    let hours = split[0]
    const mins = split[1]

    // add leading zero if needed to keep moment happy
    hours = hours.length === 1 ? `0${split[0]}` : hours

    return `${moment(newDate).format('YYYY-MM-DD')} ${hours}:${mins}`
}

const toDateString = (newDate) => {
    return moment(newDate).format('YYYY-MM-DD')
}

const DateTimeInput = ({
    t,
    form,
    field,
    label,
    description,
    validating,
    loading,
    displayErrors,
    inlineDescription,
    isStatic,
    disablePastDates,
    disableFutureDates,
    disableSetCurrentTime,
    initDisplaySetCurrentTime,
    displayTime,
    size,
    required
}) => {
    const { name, value } = field
    const { touched, errors, setFieldValue, validateField, setFieldTouched } = form
    const error = displayErrors && !validating && touched[name] && errors[name]

    const [date, setDate] = useState(value ? moment(value) : moment(Date.now()))
    const [time, setTime] = useState(value ? moment(value).format('HH:mm') : moment(Date.now()).format('HH:mm'))
    const [timeFocused, setTimeFocused] = useState(false)
    const [dateFocused, setDateFocused] = useState(false)
    const [displayCurrentTimeAction, setDisplayCurrentTimeAction] = useState(initDisplaySetCurrentTime)
    const [useCurrentTimeActive, setUseCurrentTimeActive] = useState(false)

    // set date and time state based on value changes
    useEffect(() => {
        setDate(value ? moment(value) : moment(Date.now()))
        setTime(value ? moment(value).format('HH:mm') : moment(Date.now()).format('HH:mm'))

        if (value) {
            // for some reason formik threw undefined error if the value just changed from null to valid value
            // small delay appears to help...
            const timeout = setTimeout(() => {
                setFieldTouched(name)
                validateField(name)
            }, 200)

            return () => clearTimeout(timeout)
        }
    }, [value, name, validateField, setFieldTouched, touched])

    // set and auto update current time
    useEffect(() => {
        if (useCurrentTimeActive) {
            const setCurrentTimeInterval = setInterval(() => {
                // add offset to avoid falling in to past/future before the interval updates curren time
                let offsetMinutes = 0
                if (disablePastDates) offsetMinutes = 1
                if (disableFutureDates) offsetMinutes = -1

                const presentDate = moment(Date.now())
                const presentTime = moment(Date.now()).add(offsetMinutes, 'minutes').format('HH:mm')

                const now = displayTime ? toDateTimeString(presentDate, presentTime) : toDateString(presentDate)
                const current = displayTime ? toDateTimeString(date, time) : toDateString(date)
                if (!value || now !== current) {
                    setFieldValue(name, now)
                }
            }, 100)

            return () => {
                clearInterval(setCurrentTimeInterval)
            }
        }
    }, [useCurrentTimeActive, name, value, date, time, setFieldValue, disablePastDates, disableFutureDates, displayTime])

    const setDateTime = (newDate, newTime) => {
        if (!displayTime) {
            setFieldValue(name, toDateString(newDate))
        } else {
            setFieldValue(name, toDateTimeString(newDate, newTime))
        }
    }

    const toggleCurrentTimeTime = () => {
        setUseCurrentTimeActive(!useCurrentTimeActive)
    }

    const handleTimekeeperCloseRequest = () => {
        setTimeFocused(false)
        setDateTime(date, time)
        setUseCurrentTimeActive(false)
        setDisplayCurrentTimeAction(true)
    }

    return (
        <FormElement
            name={name}
            value={value}
            label={label}
            description={description}
            error={error}
            loading={loading}
            validating={validating}
            elementClass="form-element--date-time-input"
            inlineDescription={inlineDescription}
            isStatic={isStatic}
            required={required}
        >
            {() => (
                <div className={classNames('date-time-input', size ? `date-time-input--size-${size}` : '')}>
                    <div className="date-time-input__fields">
                        <ExtendedSingleDatePicker
                            date={date.toDate()}
                            focused={dateFocused}
                            onDateChange={date => {
                                setDateTime(date, time)
                                setDisplayCurrentTimeAction(true)
                            }}
                            onFocusChange={({ focused }) => {
                                setDateFocused(focused)
                                setUseCurrentTimeActive(false)
                            }}
                            disablePastDates={disablePastDates}
                            disableFutureDates={disableFutureDates}
                        />
                        {displayTime && (
                            <>
                                <Field
                                    type="text"
                                    name="time"
                                    value={time}
                                    size="s"
                                    unitsTip={(
                                        <Icon
                                            fa
                                            name={['far', 'clock']}
                                            color="#b1b1b1"
                                        />
                                    )}
                                    component={Input}
                                    onClick={(e) => {
                                        setTimeFocused(!timeFocused)
                                        e.preventDefault()
                                    }}
                                    readOnly
                                />

                                {timeFocused && (
                                    <ExtendedTimeKeeper
                                        time={time}
                                        onChange={(data) => {
                                            setDateTime(date, data.formatted24)
                                            setUseCurrentTimeActive(false)
                                            setDisplayCurrentTimeAction(true)
                                        }}
                                        doneButton={() => (
                                            <Button
                                                className="timekeeper-done-button"
                                                trackName="selectTime"
                                                onClick={handleTimekeeperCloseRequest}
                                            >
                                                {t('common:done')}
                                            </Button>
                                        )}
                                        onCloseRequest={handleTimekeeperCloseRequest}
                                    />
                                )}
                            </>
                        )}
                    </div>
                    {(!disableSetCurrentTime && !useCurrentTimeActive && displayCurrentTimeAction) && (
                        <div className="date-time-input__actions">
                            <Button
                                trackName="useCurrentTime"
                                type="button"
                                className="button button--tertiary"
                                onClick={toggleCurrentTimeTime}
                            >
                                {t('formElements:dateTime.useCurrentTimeActivate')}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </FormElement>
    )
}

DateTimeInput.propTypes = {
    form: PropTypes.object.isRequired,
    field: PropTypes.object.isRequired,
    label: PropTypes.string,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    validating: PropTypes.bool,
    loading: PropTypes.bool,
    displayErrors: PropTypes.bool,
    inlineDescription: PropTypes.bool,
    isStatic: PropTypes.bool,
    disablePastDates: PropTypes.bool,
    disableFutureDates: PropTypes.bool,
    disableSetCurrentTime: PropTypes.bool,
    initDisplaySetCurrentTime: PropTypes.bool,
    displayTime: PropTypes.bool,
    size: PropTypes.string,
    required: PropTypes.bool,
}

DateTimeInput.defaultProps = {
    displayErrors: true,
    initDisplaySetCurrentTime: true,
    displayTime: true,
}

export default withTranslationCustom('formElements')(DateTimeInput)
